import React, { useState, useEffect, useCallback } from "react";
import {
  Globe, Users, MapPin, Calendar, Star, Phone, Mail,
  ExternalLink, ChevronDown, ChevronUp, Heart, Plane,
  RefreshCw, AlertCircle, CheckCircle, Send
} from "lucide-react";

interface DiasporaReg {
  id: string;
  refNo: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  country: string;
  city: string;
  village: string;
  profession: string;
  contribution: string;
  arrivalDate: string;
  departDate: string;
  needsAccom: boolean;
  dietaryNeeds: string;
  status: string;
  createdAt: string;
}

interface SiteSetting {
  key: string;
  value: string;
}

const COUNTRIES = [
  "United States","United Kingdom","Canada","Australia","Germany",
  "Netherlands","Italy","France","Spain","Sweden","Norway","Denmark",
  "South Africa","Ghana","Cameroon","Nigeria (International)","UAE",
  "Saudi Arabia","Qatar","Malaysia","Singapore","Other"
];

const CONTRIBUTIONS = [
  "Financial Support","Skill Transfer","Business Development",
  "Cultural Promotion","Educational Support","Healthcare",
  "Infrastructure","Technology","Youth Mentorship","Other"
];

function toast(msg: string, ok = true) {
  const el = document.createElement("div");
  el.textContent = msg;
  el.style.cssText = `position:fixed;bottom:24px;right:24px;z-index:9999;padding:12px 20px;border-radius:8px;font-size:14px;font-weight:600;color:#EDE8DC;background:${ok?"#0F5132":"#7f1d1d"};border:1px solid ${ok?"#C9A84C":"#ef4444"};box-shadow:0 4px 20px rgba(0,0,0,0.5)`;
  document.body.appendChild(el);
  setTimeout(()=>{el.style.opacity="0";el.style.transition="opacity 0.4s";setTimeout(()=>el.remove(),400);},3000);
}

const EMPTY_FORM = {
  firstName:"", lastName:"", email:"", phone:"",
  country:"", city:"", village:"", profession:"",
  contribution:"", arrivalDate:"", departDate:"",
  needsAccom:false, dietaryNeeds:""
};

export function DiasporaPortal({ currentUser }: { currentUser?: any; key?: React.Key }) {
  const [tab, setTab] = useState<"info"|"register"|"registered">("info");
  const [form, setForm] = useState({...EMPTY_FORM});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [refNo, setRefNo] = useState("");
  const [registrations, setRegistrations] = useState<DiasporaReg[]>([]);
  const [settings, setSettings] = useState<Record<string,string>>({});
  const [loadingRegs, setLoadingRegs] = useState(false);
  const [errors, setErrors] = useState<Record<string,string>>({});

  const isAdmin = currentUser?.systemRole === "PLATFORM_ADMIN" ||
                  currentUser?.email === "erueuzairue@gmail.com";

  const loadSettings = useCallback(async () => {
    try {
      const r = await fetch("/api/site-settings?group=diaspora");
      const d = await r.json();
      const map: Record<string,string> = {};
      if (Array.isArray(d)) d.forEach((s:any) => { map[s.key] = s.value; });
      setSettings(map);
    } catch {}
  }, []);

  const loadRegistrations = useCallback(async () => {
    if (!isAdmin) return;
    setLoadingRegs(true);
    try {
      const token = localStorage.getItem("erue_auth_token") || sessionStorage.getItem("erue_auth_token") || "";
      const r = await fetch("/api/diaspora/registrations", {
        headers: { "Authorization": `Bearer ${token}` }
      });
      const d = await r.json();
      setRegistrations(Array.isArray(d) ? d : []);
    } catch { setRegistrations([]); }
    finally { setLoadingRegs(false); }
  }, [isAdmin]);

  useEffect(() => {
    loadSettings();
    if (isAdmin) loadRegistrations();
  }, [loadSettings, loadRegistrations, isAdmin]);

  const validate = () => {
    const e: Record<string,string> = {};
    if (!form.firstName.trim()) e.firstName = "First name required";
    if (!form.lastName.trim()) e.lastName = "Last name required";
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = "Valid email required";
    if (!form.phone.trim()) e.phone = "Phone required";
    if (!form.country) e.country = "Country required";
    if (!form.village.trim()) e.village = "Village / hometown required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const r = await fetch("/api/diaspora/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form)
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Registration failed");
      setRefNo(d.refNo);
      setSubmitted(true);
      toast("Registration successful!");
    } catch(e:any) {
      toast(e.message || "Registration failed", false);
    } finally { setSubmitting(false); }
  };

  const f = (key: keyof typeof form, val: any) => setForm(prev => ({...prev, [key]: val}));

  const inputStyle: React.CSSProperties = {
    width:"100%", padding:"10px 12px", background:"#121E16",
    border:"1px solid #1C2E22", borderRadius:8, color:"#EDE8DC",
    fontSize:14, outline:"none", boxSizing:"border-box"
  };
  const errStyle: React.CSSProperties = {
    color:"#ef4444", fontSize:12, marginTop:4
  };
  const labelStyle: React.CSSProperties = {
    display:"block", color:"rgba(237,232,220,0.6)", fontSize:12,
    fontWeight:600, marginBottom:6, textTransform:"uppercase", letterSpacing:"0.05em"
  };

  return (
    <div style={{background:"#070D0A",minHeight:"100vh",padding:"24px",fontFamily:"Plus Jakarta Sans, sans-serif"}}>
      <div style={{maxWidth:900,margin:"0 auto"}}>

        {/* Header */}
        <div style={{textAlign:"center",marginBottom:32}}>
          <div style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:64,height:64,borderRadius:"50%",background:"#0E1812",border:"1px solid #C9A84C33",marginBottom:16}}>
            <Globe size={28} style={{color:"#C9A84C"}}/>
          </div>
          <h1 style={{fontFamily:"Cinzel,serif",color:"#C9A84C",fontSize:26,margin:"0 0 8px"}}>
            {settings.diaspora_title || "Diaspora Portal"}
          </h1>
          <p style={{color:"rgba(237,232,220,0.5)",fontSize:14,margin:0,maxWidth:600,marginLeft:"auto",marginRight:"auto"}}>
            {settings.diaspora_subtitle || "Welcome home, sons and daughters of Uzairue Kingdom. Register your participation in the ERUE Festival 2026."}
          </p>
        </div>

        {/* Tabs */}
        <div style={{display:"flex",gap:4,marginBottom:28,background:"#0E1812",borderRadius:10,padding:4,border:"1px solid #1C2E22"}}>
          {([["info","Information"],["register","Register"],isAdmin?["registered","Registrations (Admin)"] as [string,string]:null]).filter(Boolean).map(([id,label]) => (
            <button key={id!} onClick={()=>setTab(id as any)} style={{
              flex:1,padding:"10px",borderRadius:8,border:"none",cursor:"pointer",fontSize:14,fontWeight:600,
              background:tab===id?"#0F5132":"transparent",
              color:tab===id?"#C9A84C":"rgba(237,232,220,0.5)",
              transition:"all 0.2s"
            }}>{label}</button>
          ))}
        </div>

        {/* INFO TAB */}
        {tab === "info" && (
          <div>
            {/* Festival Info */}
            <div style={{background:"#0E1812",border:"1px solid #C9A84C33",borderRadius:12,padding:24,marginBottom:20,borderLeft:"4px solid #C9A84C"}}>
              <h2 style={{color:"#EDE8DC",fontFamily:"Cinzel,serif",margin:"0 0 12px",fontSize:18}}>
                ERUE Festival of Uzairue Kingdom 2026
              </h2>
              <p style={{color:"rgba(237,232,220,0.7)",fontSize:14,lineHeight:1.7,margin:"0 0 12px"}}>
                {settings.diaspora_welcome_text || "The ERUE Festival is the grandest celebration of the Uzairue Kingdom, bringing together sons and daughters from all 22 villages and component communities, as well as members of the diaspora from around the world. Join us for seven days of culture, heritage, sports, awards and community development."}
              </p>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:12,marginTop:16}}>
                {[
                  {icon:<Calendar size={16}/>,label:"Dates",value:settings.festival_dates||"December 24–30, 2026"},
                  {icon:<MapPin size={16}/>,label:"Location",value:settings.festival_location||"Palace of the Ogieneni, Jattu-Uzairue"},
                  {icon:<Star size={16}/>,label:"Theme",value:settings.festival_theme||"Uzairue Heritage: Culture, Security, Prosperity"},
                  {icon:<Users size={16}/>,label:"Grand Finale",value:settings.grand_finale||"Wednesday, 30 December 2026"},
                ].map((item,i) => (
                  <div key={i} style={{background:"#121E16",borderRadius:8,padding:"12px 16px"}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                      <span style={{color:"#C9A84C"}}>{item.icon}</span>
                      <span style={{color:"rgba(237,232,220,0.5)",fontSize:11,fontWeight:700,textTransform:"uppercase"}}>{item.label}</span>
                    </div>
                    <span style={{color:"#EDE8DC",fontSize:13}}>{item.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Why Attend */}
            <div style={{background:"#0E1812",border:"1px solid #1C2E22",borderRadius:12,padding:24,marginBottom:20}}>
              <h3 style={{color:"#C9A84C",fontFamily:"Cinzel,serif",margin:"0 0 16px",fontSize:16}}>
                Why You Should Attend
              </h3>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(250px,1fr))",gap:12}}>
                {(settings.diaspora_reasons ? JSON.parse(settings.diaspora_reasons) : [
                  "Reconnect with your roots and heritage",
                  "Network with fellow Uzairue people globally",
                  "Contribute to community development",
                  "Celebrate Uzairue culture and traditions",
                  "Meet the Royal Family and community leaders",
                  "Explore business and investment opportunities"
                ]).map((reason:string, i:number) => (
                  <div key={i} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"10px 14px",background:"#121E16",borderRadius:8}}>
                    <CheckCircle size={15} style={{color:"#C9A84C",flexShrink:0,marginTop:1}}/>
                    <span style={{color:"rgba(237,232,220,0.7)",fontSize:13,lineHeight:1.5}}>{reason}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact */}
            <div style={{background:"#0E1812",border:"1px solid #1C2E22",borderRadius:12,padding:24}}>
              <h3 style={{color:"#C9A84C",fontFamily:"Cinzel,serif",margin:"0 0 16px",fontSize:16}}>Contact the Organising Committee</h3>
              <div style={{display:"flex",gap:16,flexWrap:"wrap"}}>
                <a href={`tel:${settings.contact_phone||"+2347057840320"}`} style={{display:"flex",alignItems:"center",gap:8,color:"#EDE8DC",textDecoration:"none",fontSize:14}}>
                  <Phone size={16} style={{color:"#C9A84C"}}/> {settings.contact_phone||"+234 705 7840 320"}
                </a>
                <a href={`https://wa.me/${(settings.whatsapp_number||"2347057840320").replace(/\D/g,"")}`} target="_blank" rel="noopener noreferrer" style={{display:"flex",alignItems:"center",gap:8,color:"#25D366",textDecoration:"none",fontSize:14}}>
                  WhatsApp: {settings.whatsapp_display||"+234 705 7840 320"}
                </a>
              </div>
            </div>

            <div style={{textAlign:"center",marginTop:24}}>
              <button onClick={()=>setTab("register")} style={{padding:"14px 40px",background:"#C9A84C",border:"none",borderRadius:10,color:"#070D0A",fontWeight:700,fontSize:16,cursor:"pointer",display:"inline-flex",alignItems:"center",gap:10}}>
                <Plane size={18}/> Register Now
              </button>
            </div>
          </div>
        )}

        {/* REGISTER TAB */}
        {tab === "register" && (
          <div style={{background:"#0E1812",border:"1px solid #1C2E22",borderRadius:12,padding:28}}>
            {submitted ? (
              <div style={{textAlign:"center",padding:"40px 20px"}}>
                <CheckCircle size={56} style={{color:"#C9A84C",margin:"0 auto 16px",display:"block"}}/>
                <h2 style={{fontFamily:"Cinzel,serif",color:"#C9A84C",margin:"0 0 8px"}}>Registration Successful!</h2>
                <p style={{color:"rgba(237,232,220,0.7)",fontSize:14,margin:"0 0 16px"}}>
                  Your reference number is:
                </p>
                <div style={{background:"#121E16",border:"1px solid #C9A84C",borderRadius:8,padding:"12px 24px",display:"inline-block",marginBottom:20}}>
                  <span style={{color:"#C9A84C",fontFamily:"Cinzel,serif",fontSize:20,fontWeight:700,letterSpacing:"0.1em"}}>{refNo}</span>
                </div>
                <p style={{color:"rgba(237,232,220,0.5)",fontSize:13}}>
                  Please save this reference number. You will receive a confirmation email shortly.
                </p>
                <button onClick={()=>{setSubmitted(false);setForm({...EMPTY_FORM});setRefNo("");}} style={{marginTop:16,padding:"10px 24px",background:"transparent",border:"1px solid #C9A84C",borderRadius:8,color:"#C9A84C",cursor:"pointer",fontSize:14}}>
                  Register Another
                </button>
              </div>
            ) : (
              <>
                <h2 style={{fontFamily:"Cinzel,serif",color:"#C9A84C",margin:"0 0 20px",fontSize:18}}>Diaspora Registration Form</h2>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
                  {/* Personal */}
                  <div>
                    <label style={labelStyle}>First Name *</label>
                    <input value={form.firstName} onChange={e=>f("firstName",e.target.value)} style={{...inputStyle,borderColor:errors.firstName?"#ef4444":"#1C2E22"}}/>
                    {errors.firstName && <div style={errStyle}>{errors.firstName}</div>}
                  </div>
                  <div>
                    <label style={labelStyle}>Last Name *</label>
                    <input value={form.lastName} onChange={e=>f("lastName",e.target.value)} style={{...inputStyle,borderColor:errors.lastName?"#ef4444":"#1C2E22"}}/>
                    {errors.lastName && <div style={errStyle}>{errors.lastName}</div>}
                  </div>
                  <div>
                    <label style={labelStyle}>Email Address *</label>
                    <input type="email" value={form.email} onChange={e=>f("email",e.target.value)} style={{...inputStyle,borderColor:errors.email?"#ef4444":"#1C2E22"}}/>
                    {errors.email && <div style={errStyle}>{errors.email}</div>}
                  </div>
                  <div>
                    <label style={labelStyle}>Phone / WhatsApp *</label>
                    <input value={form.phone} onChange={e=>f("phone",e.target.value)} style={{...inputStyle,borderColor:errors.phone?"#ef4444":"#1C2E22"}}/>
                    {errors.phone && <div style={errStyle}>{errors.phone}</div>}
                  </div>
                  <div>
                    <label style={labelStyle}>Country of Residence *</label>
                    <select value={form.country} onChange={e=>f("country",e.target.value)} style={{...inputStyle,borderColor:errors.country?"#ef4444":"#1C2E22"}}>
                      <option value="">-- Select Country --</option>
                      {COUNTRIES.map(c=><option key={c} value={c}>{c}</option>)}
                    </select>
                    {errors.country && <div style={errStyle}>{errors.country}</div>}
                  </div>
                  <div>
                    <label style={labelStyle}>City</label>
                    <input value={form.city} onChange={e=>f("city",e.target.value)} placeholder="City of residence" style={inputStyle}/>
                  </div>
                  <div>
                    <label style={labelStyle}>Village / Hometown in Uzairue *</label>
                    <input value={form.village} onChange={e=>f("village",e.target.value)} placeholder="e.g. Jattu-Uzairue, Iyamho..." style={{...inputStyle,borderColor:errors.village?"#ef4444":"#1C2E22"}}/>
                    {errors.village && <div style={errStyle}>{errors.village}</div>}
                  </div>
                  <div>
                    <label style={labelStyle}>Profession</label>
                    <input value={form.profession} onChange={e=>f("profession",e.target.value)} placeholder="Your profession or occupation" style={inputStyle}/>
                  </div>
                  <div style={{gridColumn:"1/-1"}}>
                    <label style={labelStyle}>Area of Contribution to Uzairue</label>
                    <select value={form.contribution} onChange={e=>f("contribution",e.target.value)} style={inputStyle}>
                      <option value="">-- Select --</option>
                      {CONTRIBUTIONS.map(c=><option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Expected Arrival Date</label>
                    <input type="date" value={form.arrivalDate} onChange={e=>f("arrivalDate",e.target.value)} style={inputStyle}/>
                  </div>
                  <div>
                    <label style={labelStyle}>Expected Departure Date</label>
                    <input type="date" value={form.departDate} onChange={e=>f("departDate",e.target.value)} style={inputStyle}/>
                  </div>
                  <div style={{gridColumn:"1/-1"}}>
                    <label style={labelStyle}>Dietary Needs / Special Requirements</label>
                    <textarea value={form.dietaryNeeds} onChange={e=>f("dietaryNeeds",e.target.value)} rows={2} placeholder="Any dietary restrictions or special requirements..." style={{...inputStyle,resize:"vertical"}}/>
                  </div>
                  <div style={{gridColumn:"1/-1"}}>
                    <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
                      <input type="checkbox" checked={form.needsAccom} onChange={e=>f("needsAccom",e.target.checked)} style={{accentColor:"#C9A84C",width:16,height:16}}/>
                      <span style={{color:"#EDE8DC",fontSize:14}}>I need accommodation assistance</span>
                    </label>
                  </div>
                </div>
                <div style={{marginTop:24,display:"flex",justifyContent:"flex-end"}}>
                  <button onClick={submit} disabled={submitting} style={{
                    padding:"12px 36px",background:"#C9A84C",border:"none",borderRadius:10,
                    color:"#070D0A",fontWeight:700,fontSize:15,cursor:submitting?"not-allowed":"pointer",
                    opacity:submitting?0.7:1,display:"flex",alignItems:"center",gap:10
                  }}>
                    <Send size={16}/> {submitting ? "Submitting..." : "Submit Registration"}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* ADMIN TAB */}
        {tab === "registered" && isAdmin && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
              <h2 style={{color:"#EDE8DC",fontFamily:"Cinzel,serif",margin:0,fontSize:18}}>
                Diaspora Registrations ({registrations.length})
              </h2>
              <button onClick={loadRegistrations} style={{padding:"8px 14px",background:"#0E1812",border:"1px solid #1C2E22",borderRadius:8,color:"rgba(237,232,220,0.6)",cursor:"pointer",fontSize:13,display:"flex",alignItems:"center",gap:6}}>
                <RefreshCw size={13}/> Refresh
              </button>
            </div>
            {loadingRegs ? (
              <div style={{textAlign:"center",padding:40,color:"rgba(237,232,220,0.4)"}}>Loading...</div>
            ) : registrations.length === 0 ? (
              <div style={{textAlign:"center",padding:60,color:"rgba(237,232,220,0.4)"}}>
                <Globe size={40} style={{margin:"0 auto 12px",opacity:0.3,display:"block"}}/>
                <p>No registrations yet.</p>
              </div>
            ) : (
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {registrations.map(reg => (
                  <div key={reg.id} style={{background:"#0E1812",border:"1px solid #1C2E22",borderRadius:10,padding:"14px 18px"}}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
                      <div>
                        <span style={{color:"#EDE8DC",fontSize:15,fontWeight:700}}>{reg.firstName} {reg.lastName}</span>
                        <span style={{marginLeft:12,padding:"2px 8px",borderRadius:10,fontSize:11,background:reg.status==="confirmed"?"#0F5132":"#1a1a00",color:reg.status==="confirmed"?"#C9A84C":"#fbbf24",border:`1px solid ${reg.status==="confirmed"?"#C9A84C44":"#fbbf2444"}`}}>
                          {reg.status.toUpperCase()}
                        </span>
                      </div>
                      <span style={{color:"#C9A84C",fontSize:12,fontFamily:"monospace"}}>{reg.refNo}</span>
                    </div>
                    <div style={{display:"flex",gap:16,flexWrap:"wrap",marginTop:8}}>
                      <span style={{color:"rgba(237,232,220,0.5)",fontSize:13}}>{reg.country} — {reg.city}</span>
                      <span style={{color:"rgba(237,232,220,0.5)",fontSize:13}}>{reg.village}</span>
                      <span style={{color:"rgba(237,232,220,0.5)",fontSize:13}}>{reg.email}</span>
                      <span style={{color:"rgba(237,232,220,0.5)",fontSize:13}}>{reg.phone}</span>
                    </div>
                    {reg.arrivalDate && <div style={{color:"rgba(237,232,220,0.4)",fontSize:12,marginTop:4}}>Arrival: {reg.arrivalDate} → Departure: {reg.departDate}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default DiasporaPortal;
